import appInfo from "./app-info";
import xior from 'xior';
import { inject, computed } from "vue";

// We refresh the token 30 days before it expires. By default, the access token has a 60-day expiration.
const REFRESH_TOKEN_WINDOW = 30 * 24 * 60 * 60 * 1000;
const REFRESH_TOKEN_MINIMUM = 7 * 24 * 60 * 60 * 1000;

// The authentication section here uses a manual xior instance instead of the provide/inject version because
// we don't have access to setup() or anything else here at the top level where we could use inject from Vue.
const $httpRaw = xior.create({
	baseURL: (appInfo.baseURL.endsWith('/') ? appInfo.baseURL.substr(0, appInfo.baseURL.length - 1) : appInfo.baseURL),
	headers: { 'Accept': 'application/json' },
});

// TODO: Implement proper avatar image URLs later on.
const DEFAULT_AVATAR_URL = appInfo.storageURL + '/test_employee.png';

// Support for remembering password and session - the first time Auth is imported (nearly always in the root App),
// see if we have access to localStorage stored auth keys.
let startupUser = null;
let startupToken = null;
if (localStorage) {
	try {
		startupUser = localStorage.getItem(appInfo.localPrefix + 'User');
		startupUser = JSON.parse(startupUser);
		startupToken = localStorage.getItem(appInfo.localPrefix + 'Auth');
	} catch (ex) {
		startupUser = null;
		startupToken = null;
		console.error("Exception loading remembered user. Clearing login session.");
		localStorage.removeItem(appInfo.localPrefix + 'User');
		localStorage.removeItem(appInfo.localPrefix + 'Auth');
	}
}

// If the startup user/token is from a long time ago (beyond our ability to refresh it), clear it.
if (startupUser && startupToken) {
	let tokenExpired = !startupUser.token_expires || new Date(startupUser.token_expires) < Date.now();
	let tokenExpiring = !tokenExpired && new Date(startupUser.token_expires) < new Date(Date.now() + REFRESH_TOKEN_WINDOW);

	if (tokenExpiring) {
		console.log("Auth: Token expiring, starting refresh check.");
		await $httpRaw.get('/../sanctum/csrf-cookie');
		try {
			let refreshData = await $httpRaw.post('/user/refresh', {}, {
				headers: { 'Authorization': 'Bearer ' + startupToken }
			});
			if (refreshData && refreshData.data && refreshData.data.refreshed && refreshData.data.expires_at && refreshData.data.user) {
				// Sanity check that the user hasn't changed.
				if (refreshData.data.user.id !== startupUser.id) {
					throw new Error("User ID mismatch from refresh request, ignoring return refresh value.");
				}
				refreshData.data.user['avatarUrl'] = DEFAULT_AVATAR_URL;
				refreshData.data.user['token_expires'] = refreshData.data.expires_at;
				localStorage.setItem(appInfo.localPrefix + 'User', JSON.stringify(refreshData.data.user));
				// Reload the user/token from localStorage to ensure nothing weird was returned that can't survive a JSON stringify.
				startupUser = localStorage.getItem(appInfo.localPrefix + 'User');
				startupUser = JSON.parse(startupUser);
				startupToken = localStorage.getItem(appInfo.localPrefix + 'Auth');
				console.log("Auth: Refresh successful, token expiry extended.");
			} else {
				throw new Error("Invalid refresh data received.");
			}
		} catch (ex) {
			// If refresh fails and we are within our minimum window, we just mark the token as expired ahead of time.
			if (new Date(startupUser.token_expires) > new Date(Date.now() + REFRESH_TOKEN_MINIMUM)) {
				// Token still has significant time left, continue normal operation despite refresh failure
				console.error("Auth: Refresh failed, but token is still valid. Continuing normal operation.", ex);
			} else {
				// Token close to expiration and refresh failed, treat as expired
				console.error("Auth: Refresh failed and token near expiration, forcing logout.", ex);
				tokenExpired = true;
			}
		}
	}
	
	if (tokenExpired) {
		console.warn("Auth: Token expired, clearing login session.");
		startupUser = null;
		startupToken = null;
		localStorage.removeItem(appInfo.localPrefix + 'User');
		localStorage.removeItem(appInfo.localPrefix + 'Auth');
		// This should automatically trigger redirections as required if router thinks we aren't logged in.
	}
}

const AuthSystem = {
	// Note that _user & _token are not reactive!
	_user: (startupUser ? startupUser : null),
	_token: (startupToken ? startupToken : null),
	_http: null,			// HTTP instance used to reset headers when needed.
	
	// Call this whenever a possible change in user/token has been detected, to reset our $http configuration.
	// Because this uses the inject() function, it must be called after the app is initialized in a setup(). We usually do this in the root App component.
	// Once done, we store the actual http instance in _http so we can reset it when needed.
	resetHttpHeaders() {
		if (!this._http)
			this._http = inject('$http');
		if (this._user && this._token && new Date(this._user.token_expires) > Date.now()) {
			console.log("resetHttpHeaders: Detecting logged in, setting auth header.");
			this._http.config.headers['Authorization'] = 'Bearer ' + this._token;
		} else {
			console.log("resetHttpHeaders: Detecting not logged in, removing auth header.");
			this._http.config.headers['Authorization'] = '';
		}
	},
	
	isLoggedIn() {
		console.log("isLoggedIn: returning ", !!this._user);
		return !!this._user;
	},

	isAdmin: computed(() => {
		return !!AuthSystem._user && AuthSystem._user.id && AuthSystem._user.id == 1;
	}),

	// Attempt login with given email/password. Returns { isOk, message, data }.
	async logIn(email, password) {
		try {
			await $httpRaw.get('/../sanctum/csrf-cookie');
			let csrfCookie = RegExp('XSRF-TOKEN' + "=[^;]+").exec(document.cookie);
			csrfCookie = decodeURIComponent(csrfCookie ? csrfCookie.toString().replace(/^[^=]+./, "") : "");
			let tokenRequest = await $httpRaw.post('/user/login', { 'email': email, 'password': password }, {
				headers: { 'X-XSRF-TOKEN': csrfCookie }
			});
			// Return data includes user profile, token, expiration. We store token as a simple string, and combine user profile with expiration date.
			let userProfile = tokenRequest.data.user;
			userProfile['avatarUrl'] = DEFAULT_AVATAR_URL;
			userProfile['token_expires'] = tokenRequest.data.expires_at;
			this._user = userProfile;
			this._token = tokenRequest.data.token;
			localStorage.setItem(appInfo.localPrefix + 'User', JSON.stringify(userProfile));
			localStorage.setItem(appInfo.localPrefix + 'Auth', this._token);
			// TODO: We need to refresh cache's and things that rely on login for menu state here!
			
			return {
				isOk: true,
				data: this._user,
			}
		}
		catch {
			return {
				isOk: false,
				message: "Authentication failed - check your password"
			};
		}
	},

	async logOut() {
		this._user = null;
		this._token = null;
		localStorage.removeItem(appInfo.localPrefix + 'User');
		localStorage.removeItem(appInfo.localPrefix + 'Auth');
	},

	// Fetch public metadata on a user login identified by email (i.e. does it exist, potential 2FA, etc.)
	async userLoginMetadata(email) {
		try {
			await $httpRaw.get('/../sanctum/csrf-cookie');
			let csrfCookie = RegExp('XSRF-TOKEN' + "=[^;]+").exec(document.cookie);
			csrfCookie = decodeURIComponent(csrfCookie ? csrfCookie.toString().replace(/^[^=]+./, "") : "");
			let metaResult = await $httpRaw.post('/user/metalogin', { 'email': email }, {
				headers: { 'X-XSRF-TOKEN': csrfCookie }
			});
			return {
				isOk: true,
				data: metaResult.data,
			}
		} catch {
			return { isOk: false, message: "Email lookup failed" };
		}
	},

	async getUser() {
		try {
			// Send request
			console.log("Getting user...");
			return {
				isOk: true,
				data: this._user,
			};
		}
		catch {
			return {
				isOk: false
			};
		}
	},

	async resetPassword(email) {
		try {
			// Send request
			console.log(email);

			return {
				isOk: true
			};
		}
		catch {
			return {
				isOk: false,
				message: "Failed to reset password"
			};
		}
	},

	async changePassword(email, recoveryCode) {
		try {
			// Send request
			console.log(email, recoveryCode);

			return {
				isOk: true
			};
		}
		catch {
			return {
				isOk: false,
				message: "Failed to change password"
			}
		}
	},

	async createAccount(email, password) {
		try {
			await $httpRaw.get('/../sanctum/csrf-cookie');
			let csrfCookie = RegExp('XSRF-TOKEN' + "=[^;]+").exec(document.cookie);
			csrfCookie = decodeURIComponent(csrfCookie ? csrfCookie.toString().replace(/^[^=]+./, "") : "");
			let tokenRequest = await $httpRaw.post('/user/register', { 'email': email, 'password': password }, {
				headers: { 'X-XSRF-TOKEN': csrfCookie }
			});
			
			let userProfile = tokenRequest.data.user;
			userProfile['avatarUrl'] = DEFAULT_AVATAR_URL;
			this._user = userProfile;
			this._token = tokenRequest.data.token;
			localStorage.setItem(appInfo.localPrefix + 'User', JSON.stringify(userProfile));
			localStorage.setItem(appInfo.localPrefix + 'Auth', this._token);

			return {
				isOk: true,
				data: this._user,
			}
		}
		catch {
			return {
				isOk: false,
				message: "Failed to create account"
			};
		}
	}
};

export default AuthSystem;